import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, BookOpen, Check, ExternalLink } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import guide from "@/data/generated/guide.generated.json";

const SITE_URL = "https://be-parent.de";
const DEFAULT_TITLE = "BeParent – Dokumentations-App für getrennte Eltern";
const DEFAULT_DESCRIPTION =
  "BeParent hilft getrennten Eltern, Umgang, Termine, Ereignisse, Kommunikation und Dokumente strukturiert und sachlich zu dokumentieren.";

function usePageMetadata(title, description, pathname) {
  useEffect(() => {
    document.title = title;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) descriptionTag.setAttribute("content", description);

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) canonicalTag.setAttribute("href", `${SITE_URL}${pathname}`);

    return () => {
      document.title = DEFAULT_TITLE;
      if (descriptionTag) descriptionTag.setAttribute("content", DEFAULT_DESCRIPTION);
      if (canonicalTag) canonicalTag.setAttribute("href", `${SITE_URL}/`);
    };
  }, [title, description, pathname]);
}

function GuideLayout({ children }) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#0F172A] text-white">
      <Nav />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-56 -top-56 h-[620px] w-[620px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(0,250,248,0.11), transparent 62%)",
          filter: "blur(35px)",
        }}
      />
      {children}
      <Footer />
    </main>
  );
}

export function GuideOverview() {
  const [activeChapter, setActiveChapter] = useState("Alle");
  const filterListRef = useRef(null);
  const [filterOverflow, setFilterOverflow] = useState({ left: false, right: false });
  const chapters = useMemo(
    () => [...new Set(guide.articles.map((article) => article.chapter))],
    [],
  );
  const visibleArticles =
    activeChapter === "Alle"
      ? guide.articles
      : guide.articles.filter((article) => article.chapter === activeChapter);

  const updateFilterOverflow = useCallback(() => {
    const filterList = filterListRef.current;
    if (!filterList) return;

    const maxScrollLeft = filterList.scrollWidth - filterList.clientWidth;
    setFilterOverflow({
      left: filterList.scrollLeft > 2,
      right: maxScrollLeft - filterList.scrollLeft > 2,
    });
  }, []);

  useEffect(() => {
    updateFilterOverflow();
    window.addEventListener("resize", updateFilterOverflow);
    return () => window.removeEventListener("resize", updateFilterOverflow);
  }, [updateFilterOverflow]);

  usePageMetadata(
    "Ratgeber für getrennte Eltern | BeParent",
    "Verständliche Informationen zu Familienrecht, Dokumentation, Kommunikation und familienrechtlichen Verfahren.",
    "/ratgeber",
  );

  return (
    <GuideLayout>
      <section className="relative pb-14 pt-36 md:pb-20 md:pt-44">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="mb-7 flex items-center gap-4">
            <span className="font-mono-bp text-[11px] uppercase tracking-[0.35em] text-[#00FAF8]">
              Ratgeber
            </span>
            <span className="h-px flex-1 bg-gradient-to-r from-[#00FAF8]/40 via-white/10 to-transparent" />
          </div>
          <h1 className="font-display max-w-4xl text-5xl font-medium leading-[0.98] tracking-[-0.04em] md:text-7xl">
            Wissen, das <span className="text-[#00FAF8]">hilft.</span>
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-white/60 md:text-xl">
            Verständliche Informationen zu Familienrecht, Dokumentation,
            Kommunikation und familienrechtlichen Verfahren.
          </p>
        </div>
      </section>

      <section className="relative pb-28 md:pb-36">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="relative mb-10 min-w-0">
            <div
              ref={filterListRef}
              onScroll={updateFilterOverflow}
              className="flex max-w-full flex-nowrap gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="group"
              aria-label="Artikel nach Kategorie filtern"
            >
              {["Alle", ...chapters].map((chapter) => {
                const active = chapter === activeChapter;
                return (
                  <button
                    key={chapter}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setActiveChapter(chapter)}
                    className={`shrink-0 rounded-full border px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? "border-[#00FAF8] bg-[#00FAF8] text-[#0F172A]"
                        : "border-white/10 bg-white/[0.03] text-white/70 hover:border-[#00FAF8]/40 hover:text-white"
                    }`}
                  >
                    {chapter}
                  </button>
                );
              })}
            </div>

            {filterOverflow.left && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#0F172A] to-transparent"
              />
            )}
            {filterOverflow.right && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#0F172A] to-transparent"
              />
            )}
          </div>

          <p className="mb-6 font-mono-bp text-xs text-white/40" aria-live="polite">
            {visibleArticles.length} {visibleArticles.length === 1 ? "Artikel" : "Artikel"}
          </p>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {visibleArticles.map((article) => (
              <Link
                key={article.id}
                to={`/ratgeber/${article.id}`}
                className="bp-card group flex min-w-0 flex-col p-6 md:p-7"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono-bp text-[10px] uppercase tracking-[0.2em] text-[#00FAF8]">
                    {article.chapter}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-xs text-white/40">
                    <BookOpen size={14} /> {article.readingMinutes} Min.
                  </span>
                </div>
                <h2 className="font-display mt-5 break-words text-2xl font-medium leading-tight">
                  {article.title}
                </h2>
                <p className="mt-4 flex-1 text-[15px] leading-relaxed text-white/55">
                  {article.intro}
                </p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm text-[#00FAF8]">
                  Artikel lesen
                  <ArrowRight
                    size={15}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </GuideLayout>
  );
}

function formatGermanDate(value) {
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

export function GuideArticle() {
  const { id } = useParams();
  const article = guide.articles.find((entry) => entry.id === id);
  const title = article?.seo?.title || article?.title || "Artikel nicht gefunden";
  const description =
    article?.seo?.description ||
    article?.intro ||
    "Der gesuchte Ratgeber-Artikel wurde nicht gefunden.";

  usePageMetadata(`${title} | BeParent`, description, `/ratgeber/${id}`);

  if (!article) {
    return (
      <GuideLayout>
        <section className="mx-auto max-w-[900px] px-6 pb-32 pt-40 md:px-10 md:pt-48">
          <span className="font-mono-bp text-[11px] uppercase tracking-[0.3em] text-[#00FAF8]">
            Ratgeber
          </span>
          <h1 className="font-display mt-6 text-4xl font-medium md:text-6xl">
            Artikel nicht gefunden
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/60">
            Der aufgerufene Artikel existiert nicht oder ist unter dieser Adresse
            nicht verfügbar.
          </p>
          <Link
            to="/ratgeber"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#00FAF8] px-6 py-3 font-medium text-[#0F172A]"
          >
            Zur Ratgeber-Übersicht <ArrowRight size={16} />
          </Link>
        </section>
      </GuideLayout>
    );
  }

  return (
    <GuideLayout>
      <article className="relative mx-auto max-w-[900px] px-6 pb-28 pt-32 md:px-10 md:pb-36 md:pt-40">
        <Link
          to="/ratgeber"
          className="bp-link text-sm text-white/55 hover:text-[#00FAF8]"
        >
          ← Alle Artikel
        </Link>

        <header className="mt-10 border-b border-white/8 pb-10 md:pb-14">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="font-mono-bp text-[11px] uppercase tracking-[0.25em] text-[#00FAF8]">
              {article.chapter}
            </span>
            <span className="text-white/20">•</span>
            <span className="flex items-center gap-2 text-sm text-white/45">
              <BookOpen size={15} /> {article.readingMinutes} Min. Lesezeit
            </span>
          </div>
          <h1 className="font-display mt-6 break-words text-4xl font-medium leading-[1.03] tracking-[-0.035em] md:text-6xl">
            {article.title}
          </h1>
          <p className="mt-7 text-xl leading-relaxed text-white/70 md:text-2xl">
            {article.intro}
          </p>
        </header>

        <div className="py-10 md:py-14">
          <div className="space-y-6 text-[17px] leading-8 text-white/70">
            {article.body.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <section className="mt-12 rounded-[24px] border border-[#00FAF8]/20 bg-[#00FAF8]/[0.055] p-6 md:p-8">
            <h2 className="font-display text-2xl font-medium">Kernaussagen</h2>
            <ul className="mt-6 space-y-4">
              {article.takeaways.map((takeaway) => (
                <li key={takeaway} className="flex gap-3 leading-relaxed text-white/70">
                  <Check className="mt-1 shrink-0 text-[#00FAF8]" size={17} />
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </section>

          {article.laws.length > 0 && (
            <section className="mt-14" aria-labelledby="legal-bases-title">
              <div className="flex items-center gap-4">
                <h2 id="legal-bases-title" className="font-display text-3xl font-medium">
                  Rechtsgrundlagen
                </h2>
                <span className="h-px flex-1 bg-white/8" />
              </div>
              <div className="mt-7 space-y-4">
                {article.laws.map((law) => (
                  <div
                    key={`${law.citation}-${law.url}`}
                    className="min-w-0 rounded-[20px] border border-white/8 bg-white/[0.025] p-5 md:p-6"
                  >
                    <a
                      href={law.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex max-w-full items-start gap-2 break-all font-medium text-[#00FAF8] hover:underline"
                    >
                      <span>{law.citation} · {law.title}</span>
                      <ExternalLink size={15} className="mt-1 shrink-0" />
                    </a>
                    <p className="mt-3 leading-relaxed text-white/60">
                      {law.plainExplanation}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <p className="mt-10 border-l-2 border-white/15 pl-4 text-sm leading-relaxed text-white/45">
            Die Inhalte dienen der allgemeinen Information und ersetzen keine
            individuelle Rechtsberatung.
          </p>
          <p className="mt-5 font-mono-bp text-xs text-white/40">
            Zuletzt fachlich geprüft: {formatGermanDate(article.lastReviewedAt)} ·
            Rechtsgebiet: Deutschland
          </p>

          <section className="mt-14 overflow-hidden rounded-[28px] border border-white/8 bg-gradient-to-br from-[#172033] to-[#0B1120] p-7 md:p-10">
            <span className="font-mono-bp text-[10px] uppercase tracking-[0.3em] text-[#00FAF8]">
              Mit BeParent
            </span>
            <h2 className="font-display mt-4 text-3xl font-medium md:text-4xl">
              Dokumentation nicht nur verstehen, sondern strukturiert führen.
            </h2>
            <p className="mt-5 max-w-2xl leading-relaxed text-white/60">
              BeParent führt Umgang, Ereignisse, Kommunikation und Dokumente
              strukturiert an einem Ort zusammen.
            </p>
            <a
              href="/#waitlist"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#00FAF8] px-6 py-3 font-medium text-[#0F172A]"
            >
              Mehr über BeParent <ArrowRight size={16} />
            </a>
          </section>
        </div>
      </article>
    </GuideLayout>
  );
}
